import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { useTenant } from '../../contexts/TenantContext'

const UserDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const { tenant } = useTenant()

  // Get theme colors based on tenant
  const getThemeColors = () => {
    const theme = tenant?.settings?.theme || tenant?.theme || 'default'

    if (theme === 'throwball' || tenant?.slug === 'tfi') {
      return {
        primary: '#c00f15',
        primaryHover: '#c00f15',
        gradient: '#c00f15',
        primaryRgba: 'rgba(224, 17, 25, 0.1)'
      }
    }
    // Default ABFI theme (blue)
    return {
      primary: '#022a5e',
      primaryHover: '#053e88',
      gradient: 'linear-gradient(135deg, #022a5e 0%, #053e88 100%)',
      primaryRgba: 'rgba(2, 42, 94, 0.1)'
    }
  }

  const themeColors = getThemeColors()

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/user/dashboard')
      if (response.data.success) {
        setDashboardData(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error)
      // Axios interceptor handles 401 automatically
      if (error.response?.status !== 401) {
        alert('Failed to load dashboard')
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="user-dashboard" style={{
      '--primary-color': themeColors.primary,
      '--primary-hover': themeColors.primaryHover,
      '--primary-gradient': themeColors.gradient,
      '--primary-rgba': themeColors.primaryRgba
    }}>
      <div className="dashboard-header">
        <h1>Welcome, {dashboardData?.user?.name}!</h1>
        <p className="welcome-message">Here's your overview and quick actions</p>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card stat-total">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-info">
            <h3>{dashboardData?.stats?.total_players || 0}</h3>
            <p>Total Members</p>
          </div>
        </div>

        <div className="stat-card stat-active">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <h3>{dashboardData?.stats?.active_players || 0}</h3>
            <p>Active Members</p>
          </div>
        </div>

        <div className="stat-card stat-pending">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-info">
            <h3>{dashboardData?.stats?.pending_players || 0}</h3>
            <p>Pending Approval</p>
          </div>
        </div>
      </div>

      <div className="dashboard-actions">
        <Link to="/user/my-players" className="action-card">
          <i className="fas fa-list"></i>
          <h3>My Members</h3>
          <p>View and manage your registered members</p>
        </Link>

        <Link to="/user/add-player" className="action-card action-primary">
          <i className="fas fa-user-plus"></i>
          <h3>Add New Member</h3>
          <p>Register a new member</p>
        </Link>

        <Link to="/user/profile" className="action-card">
          <i className="fas fa-user-circle"></i>
          <h3>My Profile</h3>
          <p>View and edit your profile</p>
        </Link>

        <Link to="/user/events" className="action-card">
          <i className="fas fa-calendar-alt"></i>
          <h3>My Tournaments</h3>
          <p>View and participate in tournaments</p>
        </Link>
      </div>

      <style>{`
        .user-dashboard {
          padding: 2rem;
          width: 100%;
          background: #f9fafb;
          min-height: 100vh;
        }

        .dashboard-header {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          margin-bottom: 2rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 4px solid var(--primary-color, #022a5e);
        }

        .dashboard-header h1 {
          color: var(--primary-color, #022a5e);
          margin-bottom: 0.5rem;
          font-size: 2rem;
        }

        .welcome-message {
          color: #6b7280;
          font-size: 1rem;
        }

        .dashboard-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          display: flex;
          align-items: center;
          gap: 1.5rem;
          transition: all 0.3s;
          border-top: 3px solid transparent;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .stat-icon {
          font-size: 2.5rem;
          width: 70px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
        }

        .stat-total {
          border-top-color: var(--primary-color, #022a5e);
        }

        .stat-total .stat-icon {
          color: var(--primary-color, #022a5e);
          background: var(--primary-rgba, rgba(2, 42, 94, 0.1));
        }

        .stat-active {
          border-top-color: #059669;
        }

        .stat-active .stat-icon {
          color: #059669;
          background: #d1fae5;
        }

        .stat-pending {
          border-top-color: #d97706;
        }

        .stat-pending .stat-icon {
          color: #d97706;
          background: #fef3c7;
        }

        .stat-info h3 {
          font-size: 2.5rem;
          color: #111827;
          margin-bottom: 0.25rem;
          font-weight: 700;
        }

        .stat-info p {
          color: #6b7280;
          font-size: 1rem;
        }

        .dashboard-actions {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .action-card {
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          text-decoration: none;
          transition: all 0.3s;
          text-align: center;
          border: 2px solid transparent;
        }

        .action-card:hover {
          box-shadow: 0 8px 16px rgba(0,0,0,0.15);
          transform: translateY(-4px);
          border-color: var(--primary-color, #022a5e);
        }

        .action-primary {
          background: var(--primary-gradient, linear-gradient(135deg, #022a5e, #053e88));
          color: white;
        }

        .action-primary:hover {
          border-color: var(--primary-hover, #053e88);
        }

        .action-card i {
          font-size: 3rem;
          margin-bottom: 1.25rem;
          display: block;
        }

        .action-card:not(.action-primary) i {
          color: var(--primary-color, #022a5e);
        }

        .action-primary i {
          color: white;
        }

        .action-card h3 {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .action-card:not(.action-primary) h3 {
          color: #111827;
        }

        .action-primary h3 {
          color: white;
        }

        .action-card p {
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .action-card:not(.action-primary) p {
          color: #6b7280;
        }

        .action-primary p {
          color: rgba(255,255,255,0.9);
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          gap: 1rem;
        }

        .loading-container i {
          font-size: 3rem;
          color: var(--primary-color, #022a5e);
        }

        .loading-container p {
          color: #6b7280;
          font-size: 1.1rem;
        }

        @media (max-width: 768px) {
          .user-dashboard {
            padding: 1rem;
          }

          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
            padding: 1.5rem;
          }

          .dashboard-header h1 {
            font-size: 1.5rem;
          }

          .dashboard-stats {
            grid-template-columns: 1fr;
          }

          .dashboard-actions {
            grid-template-columns: 1fr;
          }

          .stat-card {
            padding: 1.5rem;
          }

          .action-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}

export default UserDashboard
